import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "./NotesPage.css";
import jwtDecode, { JwtPayload } from "jwt-decode";

interface DecodedToken extends JwtPayload {
  username: string;
}

interface FieldNotes {
  field_id: number;
  farmer_name: string;
  farmer_note: string;
  field_note: string;
}

interface NoteHistoryEntry {
  id: number;
  date: string;
  edited_by: string;
  action: string;
  note: string;
}

// Use the environment variable for backend URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const NotesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [fieldId, setFieldId] = useState<string | null>(null);
  const [notes, setNotes] = useState<FieldNotes[]>([]);
  const [history, setHistory] = useState<NoteHistoryEntry[]>([]);
  const [editingNote, setEditingNote] = useState<NoteHistoryEntry | null>(null);
  const [newNote, setNewNote] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [loading, setLoading] = useState(false); // Loading state for data fetch

  // Check if we're in "embedded" mode via ?embedded=true
  const isEmbedded = searchParams.get("embedded") === "true";

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode<DecodedToken>(token);
        setUsername(decoded.username);
      } catch (error) {
        console.error("Invalid token:", error);
        alert("Your session has expired. Please log in again.");
        localStorage.removeItem("token");
        window.location.href = "/login";
      }
    } else {
      alert("You are not logged in. Redirecting to login...");
      window.location.href = "/login";
    }
  }, []);

  // Fetch all field notes
  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/field_notes`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`, // Add token to headers
        "ngrok-skip-browser-warning": "true",
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch field notes");
        return res.json();
      })
      .then((data) => setNotes(data))
      .catch((err) => console.error("Error fetching field notes:", err))
      .finally(() => setLoading(false));
  }, []);

  // Fetch note history whenever a field is selected
  useEffect(() => {
    if (fieldId) {
      fetchNoteHistory(fieldId);
    }
  }, [fieldId]);

  // Pre-select field if field_id is in the URL query
  useEffect(() => {
    const selectedFieldId = searchParams.get("field_id");
    if (selectedFieldId) {
      setFieldId(selectedFieldId);
    }
  }, [searchParams]);

  const fetchNoteHistory = (fieldId: string) => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/notes_history/${fieldId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`, // Add token to headers
        "ngrok-skip-browser-warning": "true",
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch note history");
        return res.json();
      })
      .then((data) => setHistory(data))
      .catch((err) => console.error("Error fetching note history:", err))
      .finally(() => setLoading(false));
  };

  const handleFieldSelection = (id: string) => {
    setFieldId(id);
    setHistory([]);
    setNewNote("");
    fetchNoteHistory(id);
  };

  const handleAddNote = () => {

    if (!fieldId) {
      alert("Please select a field id before adding a note!"); // Alert to notify the user
      document.getElementById("field-dropdown")?.focus(); // Focus the dropdown to guide the user
      return; // Stop execution if no field is selected
    }


    const newNoteData = {
      field_id: fieldId,
      edited_by: username,
      action: "Added",
      note: newNote,
    };

    fetch(`${BACKEND_URL}/api/notes_history`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`, // Add token to headers
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
      body: JSON.stringify(newNoteData),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to add note");
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          alert("Note added successfully!");
          fetchNoteHistory(fieldId!);
          setNewNote("");
        }
      })
      .catch((err) => {
        console.error("Error adding note:", err);
        alert("Please select a field id before adding a note!");
      });
  };

  const handleEditNote = (note: NoteHistoryEntry) => {
    setEditingNote(note);
  };

  const handleSaveEdit = () => {
    if (editingNote) {
      const updatedNote = {
        ...editingNote,
        action: "Updated",
      };

      fetch(`${BACKEND_URL}/api/notes_history/${editingNote.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`, // Add token to headers
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
        body: JSON.stringify(updatedNote),
      })
        .then((res) => {
          if (!res.ok) throw new Error("Failed to update note");
          return res.json();
        })
        .then((data) => {
          if (data.success) {
            alert("Note updated successfully!");
            fetchNoteHistory(fieldId!);
            setEditingNote(null);
          }
        })
        .catch((err) => console.error("Error updating note:", err));
    }
  };

  const handleDeleteNote = (noteId: number) => {
    fetch(`${BACKEND_URL}/api/notes_history/${noteId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`, // Add token to headers
        "ngrok-skip-browser-warning": "true",
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to delete note");
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          alert("Note deleted successfully!");
          fetchNoteHistory(fieldId!);
        }
      })
      .catch((err) => console.error("Error deleting note:", err));
  };

  const handleExportNotes = () => {
    const csvContent = [
      ["Date", "Edited By", "Action", "Note"],
      ...history.map((entry) => [
        new Date(entry.date).toLocaleString(),
        entry.edited_by,
        entry.action,
        entry.note,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `notes_history_field_${fieldId}.csv`;
    link.click();
  };

  return (
    <div className="notes-page">
      {loading && <div className="loading-spinner">Loading...</div>}
      {!isEmbedded && (
        <>
          <h2>Field Notes</h2>
          <p>Select a field to view and manage its details and note history.</p>
        </>
      )}

      <div className="field-select-card">
        <label htmlFor="field-dropdown">Select Field:</label>
        <select
          id="field-dropdown"
          className={!fieldId ? "dropdown-highlight" : ""}
          value={fieldId || ""}
          onChange={(e) => {
            setFieldId(e.target.value);
            handleFieldSelection(e.target.value);
          }}
        >
          <option value="" disabled>
            -- Please select a field id --
          </option>
          {notes.map((note) => (
            <option key={note.field_id} value={note.field_id}>
              {`Field ${note.field_id} - ${note.farmer_name}`}
            </option>
          ))}
        </select>
        {!fieldId && <p className="error-message">Please select a field id to proceed.</p>}
      </div>

      <div className="note-history">
        <h3>Note History</h3>
        <table className="history-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Edited By</th>
              <th>Action</th>
              <th>Note</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {history.length > 0 ? (
              history.map((entry) => (
                <tr key={entry.id}>
                  <td>{new Date(entry.date).toLocaleString()}</td>
                  <td>{entry.edited_by}</td>
                  <td>{entry.action}</td>
                  <td>{entry.note}</td>
                  <td>
                    <button onClick={() => handleEditNote(entry)}>Edit</button>
                    <button onClick={() => handleDeleteNote(entry.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5}>No history available for this field.</td>
              </tr>
            )}
          </tbody>
        </table>

        
        <div className="actions">
          <label htmlFor="new-note-input">New Note:</label>
          <input
            id="new-note-input"
            type="text"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Enter a new note"
            disabled={!fieldId} // Disable if no field is selected
          />
          <button onClick={handleAddNote} disabled={!fieldId}>
            Add Note
          </button>
          <button onClick={handleExportNotes} disabled={!fieldId}>
            Export Notes
          </button>
        </div>

        {editingNote && (
          <div className="edit-note">
            <h3>Edit Note</h3>
            <label htmlFor="edit-note-textarea">Edit Note Content:</label>
            <textarea
              id="edit-note-textarea"
              value={editingNote.note}
              onChange={(e) =>
                setEditingNote({ ...editingNote, note: e.target.value })
              }
            />
            <button onClick={handleSaveEdit}>Save</button>
            <button onClick={() => setEditingNote(null)}>Cancel</button>
          </div>
        )}
      </div>

      <button
        onClick={() => {
          window.location.href = "/all-notes?embedded=true";
        }}
      >
        View All Notes
      </button>
    </div>
  );
};

export default NotesPage;
